import {useEffect,useLayoutEffect,useState} from 'react';
import {useLocation} from 'react-router-dom';
import {analyticsConsent,browserRejectsAnalytics,configureAnalytics,setAnalyticsConsent,trackAnalyticsPage,type AnalyticsConfig} from '../analytics';
import {analyticsPage,type AnalyticsSurface} from '../analytics-privacy';
import {useSession} from '../session';
import {api} from '../api';
import './analytics.css';

let configuration:Promise<AnalyticsConfig>|undefined;
function getConfiguration() {
  return configuration ??= fetch('/api/analytics/config',{credentials:'same-origin'}).then(async response=>{
    if(!response.ok) throw new Error('Analytics unavailable');
    const value=await response.json();
    return {token:typeof value.token==='string' && /^phc_[a-zA-Z0-9_]{8,200}$/.test(value.token) ? value.token : null,environment:['production','development','test'].includes(value.environment)?value.environment:'development'} as AnalyticsConfig;
  }).catch(()=>({token:null,environment:'development'}));
}

export function AnalyticsControls({surface,blocked=false,signedIn,stored,remember}:{
  surface:AnalyticsSurface;blocked?:boolean;
  /** Nobody is asked before they are signed in: there is no one to remember. */
  signedIn?:boolean;
  /** What this person already answered, wherever they answered it. */
  stored?:'allowed'|'declined'|null;
  /** Records the answer against the person rather than this browser. */
  remember?:(value:'allowed'|'declined')=>void;
}) {
  const location=useLocation(); const [config,setConfig]=useState<AnalyticsConfig>({token:null,environment:'development'});
  const [consent,setConsent]=useState(analyticsConsent); const [open,setOpen]=useState(false);
  // An answer this person gave elsewhere settles it here too.
  useEffect(()=>{ if(stored && analyticsConsent()!==stored){ setAnalyticsConsent(stored); setConsent(stored); } },[stored]);
  useEffect(()=>{let alive=true;void getConfiguration().then(value=>{if(alive)setConfig(value);});return()=>{alive=false;};},[]);
  useEffect(()=>{
    const changed=()=>setConsent(analyticsConsent()); const show=()=>setOpen(true);
    window.addEventListener('obelisk:analytics-consent',changed); window.addEventListener('obelisk:analytics-preferences',show);
    return()=>{window.removeEventListener('obelisk:analytics-consent',changed);window.removeEventListener('obelisk:analytics-preferences',show);};
  },[]);
  useLayoutEffect(()=>{
    let alive=true;
    void configureAnalytics(config,surface,blocked).then(()=>{if(alive)trackAnalyticsPage();});
    return()=>{alive=false;};
  },[config,surface,blocked,consent,location.pathname,location.search]);
  if(!config.token || blocked || signedIn===false || !analyticsPage(location.pathname+location.search,surface) || (!open && (consent!==null || browserRejectsAnalytics()))) return null;
  const choose=(value:'allowed'|'declined')=>{setAnalyticsConsent(value);setConsent(value);setOpen(false);remember?.(value);};
  return <section className="analytics-notice" aria-labelledby="analytics-title">
    <h2 id="analytics-title">Help improve Obelisk</h2>
    <p>Share optional usage and error data with PostHog. We exclude note content, names, email addresses and search text. No screen recordings.</p>
    {browserRejectsAnalytics() && <p>Your browser’s privacy preference keeps analytics off.</p>}
    <div className="analytics-notice-actions">
      <button className="btn" onClick={()=>choose('declined')}>No thanks</button>
      <button className="btn" disabled={browserRejectsAnalytics()} onClick={()=>choose('allowed')}>Allow analytics</button>
    </div>
    <small>You can change this in Analytics preferences.</small>
  </section>;
}

export function WorkspaceAnalytics() {
  const {me,loading}=useSession();
  return <AnalyticsControls
    surface="workspace"
    blocked={loading||Boolean(me?.support)}
    signedIn={loading?undefined:Boolean(me)}
    stored={me?.actor.analyticsConsent ?? null}
    remember={value=>{void api.setAnalyticsConsent(value).catch(()=>undefined);}}
  />;
}

export function AnalyticsPrivacyButton() {
  const [enabled,setEnabled]=useState(false);
  useEffect(()=>{let alive=true;void getConfiguration().then(value=>{if(alive)setEnabled(Boolean(value.token));});return()=>{alive=false;};},[]);
  return enabled ? <button type="button" className="btn btn-ghost btn-sm" onClick={()=>window.dispatchEvent(new Event('obelisk:analytics-preferences'))}>Analytics preferences</button> : null;
}
